import { Reducer } from "redux";
import { produce } from "immer";
import _ from "underscore";

import InspectionModeConverter from "../converters/inspection_mode_converter";
import { COLOR_MODE_SELECTED, ColorModeSelectedEvent } from "../../events/selection/color_mode_selected";
import { ColorMode, inspectionModeToColorMode } from "../../models/domain/enums/color_modes";
import { DEVIATION_EDITING_MODE_TOGGLED, DeviationEditingModeToggledEvent } from "../../events/selection/deviation_editing_mode_toggled";
import { FLOOR_PLAN_HIDDEN, FloorPlanHiddenEvent } from "../../events/selection/floor_plan_hidden";
import { FLOOR_PLAN_SHOWN, FloorPlanShownEvent } from "../../events/selection/floor_plan_shown";
import { EXPLORER_MODE_SWITCHED, ExplorerModeSwitchedEvent } from "../../events/selection/explorer_mode_switched";
import { INSPECTION_MODE_SELECTED, InspectionModeSelectedEvent } from "../../events/selection/inspection_mode_selected";
import { canReviewElementsInInspectionMode, InspectionMode } from "../../models/domain/enums/inspection_modes";
import { isRoutingEvent } from "../routing_events";
import { MAP_LAYER_HIDDEN, MapLayerHiddenEvent } from "../../events/selection/panels/map_panel/map_layer_hidden";
import { MAP_LAYER_SHOWN, MapLayerShownEvent } from "../../events/selection/panels/map_panel/map_layer_shown";
import { MASTERFORMAT_FILTER_SELECTED, MasterformatFilterSelectedEvent, MasterformatProgressFilter } from "../../events/selection/masterformat_filter_selected";
import { MASTERFORMAT_SELECTED, MasterformatSelectedEvent } from "../../events/selection/masterformat_selected";
import { OBSTRUCTION_MARKING_FINISHED, ObstructionMarkingFinishedEvent } from "../../events/selection/obstruction_marking_finished";
import { OBSTRUCTION_MARKING_STARTED, ObstructionMarkingStartedEvent } from "../../events/selection/obstruction_marking_started";
import { PANEL_HIDDEN, PanelHiddenEvent } from "../../events/selection/panels/panel_hidden";
import { PANEL_NODES_CHANGED, PanelNodesChangedEvent } from "../../events/selection/panels/panel_nodes_changed";
import { PANEL_SHOWN, PanelShownEvent } from "../../events/selection/panels/panel_shown";
import { PANELS_RESET, PanelsResetEvent } from "../../events/selection/panels/panels_reset";
import { PHOTO_AREA_MINIMAP_LOADED, PhotoAreaMinimapLoadedEvent } from "../../events/loaded/photo_area_minimap_loaded";
import { PHOTO_LOCATION_SELECTED, PhotoLocationSelectedEvent } from "../../events/selection/photos/photo_location_selected";
import { PHOTO_LOCATIONS_DELETED, PhotoLocationsDeletedEvent } from "../../events/viewer/photo_locations_deleted";
import { PHOTO_SESSION_SELECTED, PhotoSessionSelectedEvent } from "../../events/selection/photos/photo_session_selected";
import { POINT_CLOUD_POINT_SIZE_CHANGED, PointCloudPointSizeChangedEvent } from "../../events/selection/point_cloud_point_size_changed";
import { REVIEW_MODE_TOGGLED_OFF, ReviewModeToggledOffEvent } from "../../events/viewer/review_mode_toggled_off";
import { REVIEW_MODE_TOGGLED_ON, ReviewModeToggledOnEvent } from "../../events/viewer/review_mode_toggled_on";
import { ROTATION_LOCKED_AROUND_POINT, RotationLockedAroundPointEvent } from "../../events/viewer/rotation_locked_around_point";
import { ROTATION_UNLOCKED, RotationUnlockedEvent } from "../../events/viewer/rotation_unlocked";
import { SECTION_POINT_CLOUD_TOGGLED_OFF, SectionPointCloudToggledOffEvent } from "../../events/viewer/section_point_cloud_toggled_off";
import { SECTION_POINT_CLOUD_TOGGLED_ON, SectionPointCloudToggledOnEvent } from "../../events/viewer/section_point_cloud_toggled_on";
import { SHOW_POINT_CLOUD_TOGGLED, ShowPointCloudToggledEvent } from "../../events/selection/show_point_cloud_toggled";
import { TO_VIEWER_FLOOR, ToViewerFloorEvent } from "../../events/routing/to_viewer_floor";
import { TO_VIEWER_SCAN_DATASET, ToViewerScanDatasetEvent } from "../../events/routing/to_viewer_scan_dataset";
import { TRADE_SELECTED, TradeSelectedEvent } from "../../events/selection/trades/trade_selected";
import { VIEW_LOADED, ViewLoadedEvent } from "../../events/loaded/view_loaded";
import { DEFAULT_POINT_SIZE } from "../utilities/point_cloud_point_size";
import { WBS_AVAILABILITY_LOADED, WbsAvailabilityLoadedEvent } from "../../events/loaded/wbs/wbs_availability_loaded";
import { without } from "../utilities/general";
import { isBigBimViewerPerformanceOn } from "../utilities/big_bim_viewer_performance";

import type PageNames from "../../models/domain/enums/page_names";
import type PanelType from "../../models/domain/enums/panel_type";
import type { Vector2Like } from "type_aliases";

export type SelectionStore = {
  inspectionMode?: InspectionMode
  /** Bumped only when the user picks a mode from the Explorer's toggle. Deliberately not saved. */
  explorerModeSwitches?: number
  colorMode?: ColorMode
  deviationEditingMode?: boolean
  showPointCloud?: boolean
  pointCloudPointSize?: number
  sectionPointCloud?: boolean
  masterformat?: string
  masterformatFilter: MasterformatProgressFilter
  recipeId?: number
  recipeStepId?: number
  photoSessions: number[]
  isRotationLocked: boolean
  photoAreaMinimapSize?: Vector2Like
  fenceSelectedPhotoLocationIds?: number[]
  wbsAvailableProjects: string[]
  panelsByPage?: { [page in PageNames]?: PanelType[] }
  layers: { [key: string]: boolean }
  showReviewColors?: boolean
  obstructingGlobalId?: string
  trade: string
  isFloorPlanVisible: boolean
}

type SelectionEvents =
  | ExplorerModeSwitchedEvent
  | ToViewerFloorEvent
  | ToViewerScanDatasetEvent
  | DeviationEditingModeToggledEvent
  | InspectionModeSelectedEvent
  | ColorModeSelectedEvent
  | MasterformatSelectedEvent
  | MasterformatFilterSelectedEvent
  | PhotoAreaMinimapLoadedEvent
  | RotationLockedAroundPointEvent
  | RotationUnlockedEvent
  | ShowPointCloudToggledEvent
  | PointCloudPointSizeChangedEvent
  | SectionPointCloudToggledOffEvent
  | SectionPointCloudToggledOnEvent
  | ViewLoadedEvent
  | PhotoLocationSelectedEvent
  | WbsAvailabilityLoadedEvent
  | PhotoLocationsDeletedEvent
  | PanelNodesChangedEvent
  | PanelHiddenEvent
  | PanelShownEvent
  | ReviewModeToggledOnEvent
  | ReviewModeToggledOffEvent
  | ObstructionMarkingStartedEvent
  | ObstructionMarkingFinishedEvent
  | MapLayerHiddenEvent
  | MapLayerShownEvent
  | PanelsResetEvent
  | PhotoSessionSelectedEvent
  | TradeSelectedEvent
  | FloorPlanHiddenEvent
  | FloorPlanShownEvent

export const defaultSelection: SelectionStore = {
  showPointCloud: false,
  pointCloudPointSize: DEFAULT_POINT_SIZE,
  sectionPointCloud: false,
  isRotationLocked: false,
  wbsAvailableProjects: [],
  photoSessions: [],
  inspectionMode: InspectionMode.progress,
  masterformatFilter: {
    issuesOnly: false,
    inProgress: true,
    notStarted: true,
    completed: true
  },
  layers: {
    photos: true,
    sharedViews: true,
    clashes: false,
    floorPlan: true,
    areaOutlines: true,
  },
  trade: null,
  isFloorPlanVisible: false,
};

const reduceSelection: Reducer<SelectionStore, SelectionEvents> = (selection: SelectionStore = defaultSelection, event) => {
  return produce<SelectionStore>(selection, (selectionDraft) => {
    if (isRoutingEvent(event)) {
      if (event.payload?.query?.photoSessionId) {
        if (event.payload.query.photoSessionId.includes(",")) {
          selectionDraft.photoSessions = event.payload.query.photoSessionId.split(",").map(parseFloat);
        } else {
          const photoSessionId = parseInt(event.payload.query.photoSessionId);
          if (selection.photoSessions.length !== 1 || selection.photoSessions[0] !== photoSessionId) {
            selectionDraft.photoSessions = [photoSessionId];
          }
        }
      }
    }
    if (!selectionDraft.panelsByPage) {
      selectionDraft.panelsByPage = {};
    }
    switch (event?.type) {
      case TO_VIEWER_FLOOR:
      case TO_VIEWER_SCAN_DATASET: {
        selectionDraft.showPointCloud = false;
        break;
      }
      case INSPECTION_MODE_SELECTED: {
        selectionDraft.inspectionMode = event.payload;
        selectionDraft.colorMode = inspectionModeToColorMode(event.payload);
        break;
      }
      // Counted rather than flagged so the Explorer can tell two switches apart. The mode itself
      // is set by the inspectionModeSelected dispatched alongside this. Left out of
      // getSelectionToSave, so a reload never looks like a switch.
      case EXPLORER_MODE_SWITCHED: {
        selectionDraft.explorerModeSwitches = (selection.explorerModeSwitches || 0) + 1;
        break;
      }
      case COLOR_MODE_SELECTED: {
        selectionDraft.colorMode = event.payload;
        break;
      }
      case PHOTO_SESSION_SELECTED: {
        const photoSessions = Array.isArray(event.payload.photoSessionId) ? event.payload.photoSessionId : [event.payload.photoSessionId];
        // Re-selecting the same sessions (eg: after every floor or element load) keeps the array, so what
        // reads it doesn't rerun.
        if (!isBigBimViewerPerformanceOn() || !_.isEqual(photoSessions, selection.photoSessions)) {
          selectionDraft.photoSessions = photoSessions;
        }
        break;
      }
      case VIEW_LOADED: {
        selectionDraft.inspectionMode = InspectionModeConverter.toInspectionMode(event.payload.viewAttributes.inspectionMode);
        selectionDraft.showPointCloud = event.payload.viewAttributes.filters.pointCloudVisible;
        selectionDraft.sectionPointCloud = event.payload.viewAttributes.filters.sectionPointCloud;
        break;
      }
      case SHOW_POINT_CLOUD_TOGGLED: {
        selectionDraft.showPointCloud = !selectionDraft.showPointCloud;
        break;
      }
      case POINT_CLOUD_POINT_SIZE_CHANGED: {
        selectionDraft.pointCloudPointSize = event.payload;
        break;
      }
      case SECTION_POINT_CLOUD_TOGGLED_OFF: {
        selectionDraft.sectionPointCloud = false;
        break;
      }
      case SECTION_POINT_CLOUD_TOGGLED_ON: {
        selectionDraft.sectionPointCloud = true;
        break;
      }
      case DEVIATION_EDITING_MODE_TOGGLED: {
        selectionDraft.deviationEditingMode = !selectionDraft.deviationEditingMode;
        break;
      }
      case MASTERFORMAT_SELECTED: {
        selectionDraft.masterformat = event.payload.masterformat;
        selectionDraft.recipeId = event.payload.recipeId;
        selectionDraft.recipeStepId = event.payload.recipeStepId;
        break;
      }
      case MASTERFORMAT_FILTER_SELECTED: {
        for (const key in event.payload) {
          selectionDraft.masterformatFilter[key] = event.payload[key];
        }
        break;
      }
      case ROTATION_LOCKED_AROUND_POINT: {
        selectionDraft.isRotationLocked = true;
        break;
      }
      case ROTATION_UNLOCKED: {
        selectionDraft.isRotationLocked = false;
        break;
      }
      case PHOTO_AREA_MINIMAP_LOADED: {
        selectionDraft.photoAreaMinimapSize = event.payload;
        break;
      }
      case PHOTO_LOCATIONS_DELETED: {
        if (event.payload) {
          selectionDraft.fenceSelectedPhotoLocationIds = [];
        }
        break;
      }
      case PHOTO_LOCATION_SELECTED: {
        if (event.payload.fenceSelectedPhotoLocationIds == null) {
          // Clear fence selected ids if primary selected photo isn't in the fence selectionStore
          if (selection.fenceSelectedPhotoLocationIds != null
              && !selection.fenceSelectedPhotoLocationIds.includes(event.payload.photoLocationId)) {
            selectionDraft.fenceSelectedPhotoLocationIds = [];
          }
        } else {
          selectionDraft.fenceSelectedPhotoLocationIds = event.payload.fenceSelectedPhotoLocationIds;
        }
        break;
      }
      case WBS_AVAILABILITY_LOADED: {
        if (event.payload.isWbsAvailable) {
          selectionDraft.wbsAvailableProjects = _.union(selection.wbsAvailableProjects, [event.payload.projectId]);
        }
        break;
      }
      case PANEL_HIDDEN: {
        selectionDraft.panelsByPage[event.payload.page] = without(selectionDraft.panelsByPage[event.payload.page], [event.payload.panel]);
        break;
      }
      case PANEL_SHOWN: {
        if (!selectionDraft.panelsByPage[event.payload.page]) {
          selectionDraft.panelsByPage[event.payload.page] = [];
        }
        selectionDraft.panelsByPage[event.payload.page].push(event.payload.panel);
        break;
      }
      case PANELS_RESET: {
        selectionDraft.panelsByPage[event.payload.page] = [];
        break;
      }
      case PANEL_NODES_CHANGED: {
        const sameNumberOfPanels = selection.panelsByPage?.[event.payload.page]?.length === event.payload.panels.length;
        const allPanelsAlreadyVisible = _.every(selection.panelsByPage?.[event.payload.page], (panel) => event.payload.panels.includes(panel));
        if (sameNumberOfPanels && allPanelsAlreadyVisible) {
          return selection;
        } else {
          return {
            ...selection,
            panelsByPage: {
              ...selection.panelsByPage,
              [event.payload.page]: event.payload.panels
            }
          };
        }
      }
      case MAP_LAYER_HIDDEN: {
        selectionDraft.layers[event.payload.layerId] = false;
        break;
      }
      case MAP_LAYER_SHOWN: {
        selectionDraft.layers[event.payload.layerId] = true;
        break;
      }
      case REVIEW_MODE_TOGGLED_ON: {
        selectionDraft.showReviewColors = true;
        break;
      }
      case REVIEW_MODE_TOGGLED_OFF: {
        selectionDraft.showReviewColors = false;
        break;
      }
      case OBSTRUCTION_MARKING_STARTED: {
        selectionDraft.obstructingGlobalId = event.payload.obstructingElementGlobalId;
        break;
      }
      case OBSTRUCTION_MARKING_FINISHED: {
        selectionDraft.obstructingGlobalId = null;
        break;
      }
      case TRADE_SELECTED: {
        selectionDraft.trade = event.payload.code;
        break;
      }
      case FLOOR_PLAN_HIDDEN: {
        selectionDraft.isFloorPlanVisible = false;
        break;
      }
      case FLOOR_PLAN_SHOWN: {
        selectionDraft.isFloorPlanVisible = true;
        break;
      }
    }
  });
};

export function getSelectionToSave(store: SelectionStore) {
  return {
    ...defaultSelection,
    inspectionMode: store.inspectionMode,
    pointCloudPointSize: store.pointCloudPointSize,
  };
}

export default reduceSelection;
